/**
 * Component sizing tokens derived from the Figma design system.
 * Source of truth: Pangea Galapagos UI Design System — Beta
 * https://www.figma.com/design/Orc6uUodQaoXep59CGaUNw
 *
 * Icon sizes come from the "Bottom nav" component variants (node 2142:278).
 * Avatar sizes come from the "Profile picture" component set.
 * Nav dimensions come from the pill nav variants.
 */

import { spacing } from './spacing';

export const sizing = {
  /** Nominal measured loading highlight width, clamped to its container. */
  skeleton: { highlightWidth: 240 },
  /**
   * Page column for web surfaces: the widest a centred single column of page
   * content grows, at every viewport width. 448px is the pixel form of the
   * 28rem every `apps/web` page shell hand-rolled before this element existed.
   * No Figma name yet — flagged in the web-registration legacy-value inventory
   * until design names it.
   */
  column: {
    max: 448,
    /**
     * The wider cap for long-form reading pages (legal documents, articles):
     * after the 16px screen-edge gutters it leaves 668px of text, about 90
     * characters of 16px body copy, and room for four-column tables. Set by
     * product at 700px. No Figma name yet.
     */
    reading: 700,
    /**
     * The web registration header's progress bar on desktop, centred between
     * the logo and the language switch (v9 Site header 131:3656). No Figma
     * name yet.
     */
    progress: 320,
  },
  sheet: {
    /**
     * Preserved list content budget from the existing native picker. This
     * compatibility value keeps the total sheet height unchanged when its
     * visible title is removed; it is not a new Figma-derived measurement.
     * Design sign-off includes the legacy value and variant naming.
     */
    listContentHeight: 420,
  },
  icon: {
    /** Tiny glyph — 12px (e.g. the small chevron inside a disclosure button). */
    xxs: 12,
    /** Extra-small icon — 16px. */
    xs: 16,
    /** Compact glyph — 18px (e.g. CAB card media and progress dots). */
    compact: 18,
    /** Small icon — 24px (e.g. inactive tab icon). */
    sm: 24,
    /** Default icon — 32px (e.g. active tab icon). */
    md: 32,
  },
  progressDot: {
    /** Existing compact progress-dot diameter. */
    compact: 12,
    /** CAB large progress-dot diameter. */
    large: 18,
  },
  avatar: {
    /** Compact inline avatar — 16px (e.g. provider identity beside appointment copy). */
    compact: 16,
    /** Medium avatar — 40px (e.g. the overlapping faces on a chat-entry row). */
    md: 40,
    /** Large avatar — 64px (e.g. profile header). */
    lg: 64,
    /** Extra-large avatar — 80px (e.g. care-team card + care-specialist header). */
    xl: 80,
  },
  touchTarget: {
    /** Minimum touch target for interactive controls. */
    minimum: 48,
  },
  stroke: {
    /** Hairline rule — 1px on web; native rules use StyleSheet.hairlineWidth. */
    hairline: 1,
    /** Strong outline — 2px (e.g. the border of a recommended-action card). */
    strong: 2,
    /** Emphasis rule — 3px (e.g. an active-tab underline indicator). */
    emphasis: 3,
  },
  controlButton: {
    /** Small circular icon button — 36px (e.g. sheet close). */
    sm: 36,
    /** Default circular icon button — 44px (e.g. card forward-arrow). */
    md: 44,
    /** Large circular status disc — 72px (e.g. booking-confirmed tick). */
    lg: 72,
  },
  daySummary: {
    /** Available width in the standalone Figma component reference frame. */
    referenceWidth: 339,
    /** Maximum day-cell width in the initial-review recap frame. */
    cellMaxWidth: 45,
    /** Nine equal cells at the 339px reference content width, including 4px gaps. */
    cellMinWidth: 34,
  },
  legend: {
    /** Compact swatch width from the recap legend. */
    compactSwatchWidth: 17,
    /** Compact swatch height from the recap legend. */
    compactSwatchHeight: 22,
    /** Compact swatch-to-label gap from the recap legend. */
    compactGap: 7,
  },
  stepCounter: {
    /** Default step circle diameter — Figma conversation-progress counter. */
    circle: 30,
    /** Tick glyph diameter inside the default step circle. */
    tick: 17,
  },
  illustration: {
    /** Sheet / callout illustration — 148px (Figma "Three speech bubbles"). */
    sm: 148,
    /** Illustration rendered inside a panel — 133px. */
    md: 133,
    /** Full-width screen illustration — 232px (Figma "Walking"). */
    lg: 232,
    /** Lavender illustration-panel height — 160px. */
    panelHeight: 160,
  },
  bottomNav: {
    /** Left pill width (Home + Profile). */
    leftPillWidth: 175,
    /** Right pill width (Chat). */
    rightPillWidth: 70,
    /** Pill height — matches Figma 68px. */
    pillHeight: 68,
    /** Active highlight inset height. */
    highlightHeight: 60,
  },
  dialog: {
    /**
     * Widest a dialog grows on a desktop viewport — the "Web/Dialog" frame
     * (Figma hk9NaJrKRwkoGmmZ5rxbsE, node 49:5525), whose width binds no
     * variable. No UI Design System library name yet, so the key may be
     * renamed once design names it there.
     */
    maxWidth: 480,
  },
  dateInput: {
    /**
     * Fixed width of the year box in a date-of-birth input — the "Web/Date of
     * birth input" frame (Figma hk9NaJrKRwkoGmmZ5rxbsE, node 47:5424), whose
     * width binds no variable. No UI Design System library name yet, so the
     * key may be renamed once design names it there.
     */
    yearWidth: 140,
  },
  buttonCard: {
    /** Fixed compact (button + text) frame width — matches the Figma "Button card" (116px). */
    compactWidth: 116,
  },
  toolCard: {
    /** Fixed tile width — matches the Figma "Tool card" component (138px). */
    width: 138,
    /** Fixed tile height — matches the Figma "Tool card" component (166px). */
    height: 166,
  },
  metricCard: {
    /** Minimum indicator-card height; content may expand for accessibility text scaling. */
    indicatorMinHeight: 128,
  },
  featureCard: {
    /** Minimum height without an illustration — the Figma "Reflection card" frame's
     * own height (3605:19763). A floor for short copy, not a fixed size: a card
     * whose content needs more grows past it. Set above the frame it stops being
     * a floor and becomes dead space under the body. */
    minHeight: 182,
    /** Minimum height with an illustration, so the media row has room — the
     * Figma "Practice card" frame (3605:19760). */
    minHeightWithImage: 382,
    /** Illustration edge length — ~50% of the with-image card height. */
    illustration: 180,
    /** Reward-badge glyph edge length. */
    badgeIcon: 20,
    /** Width of the Figma frame the card's decorative geometry is authored
     * against. Paired with `minHeight`, it is the coordinate system the wedge
     * mask's paths are expressed in — the layer is stretched to the rendered
     * card, so this is a reference frame rather than a rendered size. */
    frameWidth: 370,
  },
  mediaPreview: {
    /** Maximum inline-media width in a conversational surface. */
    maxWidth: 288,
    /** Maximum inline-media height in a conversational surface. */
    maxHeight: 240,
    /** Maximum fraction of the viewport available to a message bubble. */
    maxWidthRatio: 0.8,
  },
  slider: {
    /** Handle (slider button) diameter — Figma dark circle. */
    handle: 60,
    /** Track (tracker) height — Figma. */
    track: 44,
    /** Tall track length — Figma Assessment q2 vertical motivation scale. */
    verticalTrack: 386,
  },
  weekSummary: {
    /** Day chip height and figure tile width — Figma Web/Week summary (69:323), 44px. */
    control: 44,
    /** Figure tile height, room for its 28/34 number — Figma Web/Week summary (69:323), 64px. */
    figureHeight: 64,
  },
  videoCall: {
    /** In-call local picture-in-picture width — Figma in-call frame (108px). */
    pipWidth: 108,
    /** Portrait video frame width:height ratio — Figma PiP and test-screen card. */
    aspectRatio: 0.57,
    /** Pre-call preview card height as a fraction of screen height — Figma 473px card on an 874px frame. */
    previewHeightRatio: 0.54,
    /** Bottom scrim gradient height behind the in-call controls — Figma (288px). */
    scrimHeight: 288,
  },
} as const;

export type Sizing = typeof sizing;

// ---------------------------------------------------------------------------
// NumberPicker row geometry. Defined here (and re-exported by the NumberPicker
// module, whose API is unchanged) because flow data needs these to size column
// headers that must match the stepper row exactly, via the `./sizing` subpath,
// without pulling the root barrel and its native component graph.
// ---------------------------------------------------------------------------

// Stepper button dimensions
export const STEPPER_BUTTON_SIZE = spacing.xxl; // 40px
export const STEPPER_GAP = spacing.s;

// Shared column width for the read-only reference tile (comparison screens
// only) — wide enough to also fit that column's header label ("Last 7 days")
// without wrapping. The compact VALUE_DISPLAY_MIN_WIDTH tile centers inside
// this wider column on data rows; the column header centers its label in an
// identical column, so both align by construction rather than by eyeballed
// offsets, regardless of either the tile's or the label's own content width.
export const REFERENCE_COLUMN_WIDTH = 120;

// The inline editable field is a fixed 56×44 box (Figma), distinct from the
// shared VALUE_DISPLAY_MIN_WIDTH the read-only reference tile/centered display
// use — no exact spacing-scale rung covers 56 either.
export const INLINE_VALUE_WIDTH = 56;

// Total width of the inline stepper row (decrement button + gap + value field +
// gap + increment button) — the single source of truth for anything that must
// match this row's width exactly (e.g. a column header sized to match it).
export const INLINE_STEPPER_ROW_WIDTH =
  STEPPER_BUTTON_SIZE * 2 + STEPPER_GAP * 2 + INLINE_VALUE_WIDTH;
